import React, { useEffect, useState, useRef, useLayoutEffect } from 'react'
// import { getDeviceType, SMALL } from '@fil-global/gds-components/scripts/util/util'
import queryString from 'query-string'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { checkIsYoungFund } from 'utils'
import { reduceItemsByFilter } from 'utils/filter'
import { updateUrlWithParams } from 'utils/url'
import { dispatchFundCompareEvent, dispatchFundComparePage } from 'utils/tracking'

import {
  getFundList,
  getFundsComparisonListData,
  getFundsComparisonPerformanceListData,
  getFundsComparisonPerformanceChartData,
  getSplittingWords
} from '../services'
import { formatComparisonData, formatComparisonPerformanceData, formatComparisonChartData } from '../data.format'

import chartConfig from './chartConfig'

import HomepageLevelMessage from '../../../atoms/HomepageLevelMessage'
import FundsCompare from '../../../templates/FundsCompare'

import './style.scss'

const TWFundsCompare = () => {
  const history = useHistory()
  const { comparisonLimit, fundComparisonCart, countryConfig } = getGlobalParams()
  const intl = getCustomIntl()
  const isMobile = () => {
    const isClient = typeof window !== 'undefined'
    const viewPortWidth = isClient ? window.innerWidth : -1

    // const breakpoint = getDeviceType()
    // return breakpoint === SMALL

    return viewPortWidth < 700
  }

  const [isMobileSize, setScreenSize] = useState(false)
  const [currentFundsError, setCurrentFundsError] = useState()
  const [dropdownFundsList, setDropdownFundsList] = useState()
  const [comparisonCardData, setComparisonCardData] = useState([])
  const [comparisonOriginalData, setComparisonOriginalData] = useState([])
  const [comparisonData, setComparisonData] = useState({})
  const [savedFundsList, saveFundsList] = useState()
  const [isChartError, setChartError] = useState(false)
  const [isPerformanceTableError, setPerformanceTableError] = useState(false)
  const [isComparisonTableError, setComparisonTableError] = useState(false)
  const [isFundAdding, setFundAdding] = useState(false)
  const [originalChartData, setOriginalChartData] = useState([])
  const [performanceChartConfig, setPerformanceChartConfig] = useState()

  const comparisonCardDataRef = useRef(comparisonCardData)
  const setComparisonCardDataRef = (x) => {
    comparisonCardDataRef.current = x
    setComparisonCardData(x)
  }

  const fundsListRef = useRef(savedFundsList)
  const setFundsListRef = (x) => {
    fundsListRef.current = x
    saveFundsList(x)
  }

  const splittingWordsRef = useRef([])
  const setSplittingWordsRef = (x) => {
    splittingWordsRef.current = x
  }

  const dropdownFundsListRef = useRef(dropdownFundsList)
  const setDropdownFundsListRef = (x) => {
    dropdownFundsListRef.current = x
    setDropdownFundsList(x)
  }

  const performanceChartConfigRef = useRef(performanceChartConfig)
  const setPerformanceChartConfigRef = (x) => {
    performanceChartConfigRef.current = x
    setPerformanceChartConfig(x)
  }

  const deepClone = (preData) => JSON.parse(JSON.stringify(preData))

  const updateNewUrl = (selectedScId) => {
    const { scIds } = queryString.parse(window.location.search)
    let scIdsArray = scIds ? scIds.split(',') : []

    if (scIdsArray.includes(selectedScId)) {
      scIdsArray = scIdsArray.filter((scId) => selectedScId !== scId)
    } else {
      scIdsArray.push(selectedScId)
    }

    updateUrlWithParams({ query: { scIds: scIdsArray } })
  }

  const getMobileSizeConfig = () => {
    const mobileStatus = isMobile()
    setScreenSize(mobileStatus)
    const legend = mobileStatus
      ? { enabled: false }
      : {
          enabled: true,
          align: 'left',
          x: 16
        }
    return { legend }
  }

  const getChartsData = async (scIds, youngFunds) => {
    const mobileSizeConfig = getMobileSizeConfig()
    const chartData = await getFundsComparisonPerformanceChartData(scIds)
    if (chartData) {
      const allChartData = [...originalChartData, ...chartData].map((item) =>
        youngFunds.includes(item.scId)
          ? {
              ...item,
              series: [{ ...item.series[0], data: [] }]
            }
          : item
      )
      const maxDateChart = _.maxBy(allChartData, (data) => new Date(data.date).getTime())
      const formattedChartData = formatComparisonChartData(allChartData, {
        ...chartConfig,
        ...mobileSizeConfig,
        updateDate: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.updateDate' }, { updateDate: maxDateChart.date })
      })
      setOriginalChartData(allChartData)
      setPerformanceChartConfigRef(formattedChartData)
    } else {
      setChartError(true)
    }
  }

  /* eslint-disable no-useless-return */
  const handleResponseDataWithoutScId = (scIds, responseData) => {
    const scIdsArray = scIds.split(',')
    const missingScIdsArray = []
    scIdsArray.forEach((scId) => {
      let scIdisMissing = true
      responseData.forEach((data) => {
        if (scId === data.scId) {
          scIdisMissing = false
        }
      })
      if (scIdisMissing) {
        missingScIdsArray.push(scId)
      }
    })

    if (missingScIdsArray.length > 0) {
      const newResponseData = []
      responseData.forEach((data) => {
        if (data.scId) {
          newResponseData.push(data)
        }
      })

      missingScIdsArray.forEach((missingScId) => {
        newResponseData.push({
          scId: missingScId,
          details: []
        })
      })
      return newResponseData
    }
    return responseData
  }

  const getFundsData = async (scIds) => {
    const scIdsArray = scIds.split(',')
    setFundAdding(true)
    const formattedComparisonData = await getFundsComparisonListData(scIds)
    const youngFunds = formattedComparisonData
      .filter((dataItem) =>
        countryConfig.specialTreatmentISINsOfYoungFund.includes(dataItem.isin) ? false : checkIsYoungFund(dataItem.shareclassLaunchDate)
      )
      .map((dataItem) => dataItem.scId)

    let formattedComparisonPerformanceData = null
    if (!formattedComparisonData) {
      setFundAdding(false)
      setComparisonTableError(true)
    } else {
      getChartsData(scIds, youngFunds)
      const tempFormattedComparisonPerformanceData = await getFundsComparisonPerformanceListData(scIds)
      setFundAdding(false)

      if (!tempFormattedComparisonPerformanceData) {
        setPerformanceTableError(true)
      } else {
        formattedComparisonPerformanceData = handleResponseDataWithoutScId(scIds, tempFormattedComparisonPerformanceData)
      }
    }

    const newComparisonData = scIdsArray.map((scId) => {
      const specificData = _.find(formattedComparisonData, (data) => data.scId === scId)
      const specificPerformanceData = _.find(formattedComparisonPerformanceData, (data) => data.scId === scId)

      return {
        shareClassId: scId,
        responseData: {
          formattedComparisonData: specificData,
          formattedComparisonPerformanceData: specificPerformanceData
        }
      }
    })

    return newComparisonData
  }

  const formatAllComparisonData = (originData) => {
    const comparisonTableData = {
      ...formatComparisonData(originData),
      ...formatComparisonPerformanceData(originData)
    }

    return comparisonTableData
  }

  const formatSortFund = (fundsList) => {
    const mapFunc = (fundItem) => ({
      shareClassId: fundItem.shareClassId,
      shareClassName: fundItem.shareClassName.zh,
      isSelected: !!comparisonCardDataRef.current.find((cardItem) => cardItem.shareClassId === fundItem.shareClassId)
    })

    return _(fundsList).map(mapFunc).orderBy('isSelected', 'desc').value()
  }

  const formatRecentViewFunds = () => {
    const recentViewFundsStorageName = `${window.location.hostname}_recentViewFunds`
    const recentViewFunds = sessionStorage.getItem(recentViewFundsStorageName)
      ? JSON.parse(sessionStorage.getItem(recentViewFundsStorageName))
      : []
    let recentFunds = []
    if (recentViewFunds.length) {
      const formattedFunds = formatSortFund(
        _(fundsListRef.current)
          .filter((fundItem) => !!_.find(recentViewFunds, (recentFund) => recentFund.scId === fundItem.shareClassId))
          .value()
      )
      recentFunds = [
        { shareClassName: intl.formatMessage({ id: 'fundComparison.fundComparePage.recentViewed' }), isVisitedLabel: true },
        ...formattedFunds
      ]
    }

    return recentFunds
  }

  const getDropdownList = (value) => {
    let filteredFunds = []
    if (value) {
      const trimmedValue = value.trim()
      const filterData = {
        blocks: [
          {
            elements: [
              {
                key: 'shareClassName.zh||shareClassName.en||isin||taCode',
                type: 'input',
                wholeMatchKeys: ['taCode'],
                value: trimmedValue,
                splittingWords: splittingWordsRef.current,
                dividingWord: '#!'
              }
            ]
          }
        ]
      }
      filteredFunds = formatSortFund(reduceItemsByFilter(fundsListRef.current, filterData))
    } else {
      filteredFunds = formatRecentViewFunds()
    }

    setDropdownFundsListRef(filteredFunds)
  }

  const handleFundsComparisonItems = (fundItems) => {
    let currentItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []
    if (!Array.isArray(fundItems)) {
      const isExist = !!_.find(currentItems, (item) => item.shareClassId === fundItems.shareClassId)

      if (isExist) {
        _.remove(currentItems, (item) => item.shareClassId === fundItems.shareClassId)
      } else {
        currentItems.push({
          shareClassId: fundItems.shareClassId,
          shareClassName: fundItems.shareClassName
        })
      }
    } else {
      currentItems = fundItems
    }

    sessionStorage.setItem(fundComparisonCart, JSON.stringify(currentItems))
  }

  const addComparisonCardData = async (fundItem) => {
    const newComparisonCardData = deepClone(comparisonCardDataRef.current)
    updateNewUrl(fundItem.shareClassId)
    newComparisonCardData.push({ shareClassId: fundItem.shareClassId, shareClassName: fundItem.shareClassName, isSelected: true })
    const fundData = await getFundsData(fundItem.shareClassId)
    const formattedAllComparionData = formatAllComparisonData([...comparisonOriginalData, ...fundData])

    setComparisonOriginalData([...comparisonOriginalData, ...fundData])
    setComparisonCardDataRef(newComparisonCardData)
    setComparisonData(formattedAllComparionData)
    getDropdownList()
    handleFundsComparisonItems(fundItem)
    dispatchFundCompareEvent('compare - compare this fund', fundItem.shareClassId)
  }

  const cancelComparisonCardData = (fundItem) => {
    updateNewUrl(fundItem.shareClassId)
    const newComparisonCardData = deepClone(comparisonCardDataRef.current)
    const newComparisonOriginalData = deepClone(comparisonOriginalData)
    const newOriginalChartData = deepClone(originalChartData)

    const filteredComparisonCardData = newComparisonCardData.filter((item) => item.shareClassId !== fundItem.shareClassId)
    const filteredComparisonOriginalData = newComparisonOriginalData.filter((item) => item.shareClassId !== fundItem.shareClassId)
    const formattedAllComparionData = !_.isEmpty(filteredComparisonOriginalData)
      ? formatAllComparisonData(filteredComparisonOriginalData)
      : {}

    const filteredComparisonChartData = newOriginalChartData.filter((item) => item.scId !== fundItem.shareClassId)
    const formattedComparisonChartData = !_.isEmpty(filteredComparisonChartData)
      ? formatComparisonChartData(filteredComparisonChartData, {
          ...chartConfig,
          updateDate: intl.formatMessage(
            { id: 'fundDetail.tabs.performanceTab.updateDate' },
            { updateDate: filteredComparisonChartData[0].date }
          )
        })
      : null

    setOriginalChartData(filteredComparisonChartData)
    setPerformanceChartConfigRef(formattedComparisonChartData)

    setComparisonOriginalData(filteredComparisonOriginalData)
    setComparisonCardDataRef(filteredComparisonCardData)
    setComparisonData(formattedAllComparionData)
    getDropdownList()
    handleFundsComparisonItems(fundItem)
  }

  const handleResize = () => {
    if (!_.isEmpty(comparisonCardDataRef.current) && !_.isEmpty(performanceChartConfigRef.current)) {
      const mobileSizeConfig = getMobileSizeConfig()
      setPerformanceChartConfigRef({ ...performanceChartConfigRef.current, ...mobileSizeConfig })
    }
  }

  const handleInput = (value) => {
    getDropdownList(value)
  }

  useEffect(() => {
    if (savedFundsList) {
      const formattedRecentViewFunds = formatRecentViewFunds()
      setDropdownFundsListRef(formattedRecentViewFunds)
    }
  }, [savedFundsList])

  useLayoutEffect(() => {
    const mobileStatus = isMobile()
    setScreenSize(mobileStatus)
  }, [])

  useEffect(async () => {
    window.addEventListener('resize', handleResize)
    setFundAdding(true)
    const fullFundListPromise = getFundList()
    const splittingWordsPromise = getSplittingWords()

    const [fullFundList, splittingWords] = await Promise.all([fullFundListPromise, splittingWordsPromise])

    if (!fullFundList) {
      setCurrentFundsError(true)
      setFundAdding(false)
    } else {
      const { scIds } = queryString.parse(history.location.search)

      if (scIds) {
        const scIdsArray = scIds.split(',')
        const mapFunc = (fundItem) => ({
          shareClassId: fundItem.shareClassId,
          shareClassName: fundItem.shareClassName.zh,
          isSelected: true
        })

        const newComparisonData = await getFundsData(scIds)
        const formattedAllComparionData = formatAllComparisonData(newComparisonData)

        const initComparisonCardData = _(fullFundList)
          .filter((fundItem) => scIdsArray.includes(fundItem.shareClassId))
          .map(mapFunc)
          .sortBy((fundItem) => scIdsArray.indexOf(fundItem.shareClassId))
          .value()

        setComparisonCardDataRef(initComparisonCardData)
        setComparisonOriginalData(newComparisonData)
        setComparisonData(formattedAllComparionData)

        const currentItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []

        if (!currentItems.length) {
          handleFundsComparisonItems(
            initComparisonCardData.map((item) => ({
              shareClassId: item.shareClassId,
              shareClassName: item.shareClassName
            }))
          )
        }
      } else {
        sessionStorage.removeItem(fundComparisonCart)
      }
      setFundAdding(false)
      setFundsListRef(fullFundList)
      setSplittingWordsRef(splittingWords)
      dispatchFundComparePage()
    }
  }, [])

  return (
    <div className="tw-fil-fund-compare">
      {currentFundsError || isComparisonTableError ? (
        <HomepageLevelMessage type="fundCompare" />
      ) : (
        <FundsCompare
          id="tw-compare"
          performanceChartConfig={performanceChartConfigRef.current}
          comparisonData={comparisonData}
          comparisonCardData={comparisonCardDataRef.current}
          addComparisonCardData={addComparisonCardData}
          cancelComparisonCardData={cancelComparisonCardData}
          comparisonLimit={comparisonLimit}
          handleInput={handleInput}
          dropdownFundsList={dropdownFundsListRef.current}
          selectedCount={comparisonCardDataRef.current.length}
          isPerformanceTableError={isPerformanceTableError}
          isChartError={isChartError}
          isFundAdding={isFundAdding}
          isMobileSize={isMobileSize}
        />
      )}
    </div>
  )
}

TWFundsCompare.propTypes = {}

TWFundsCompare.defaultProps = {}

export default TWFundsCompare
